import React, { useState } from "react";
import { ArrowRight, Check, Plus, Minus } from "lucide-react";

const openAudit = () => window.dispatchEvent(new Event("open-audit"));

/* ---------- PackageGrid + PackageCard ---------- */

export function PackageGrid({
  id,
  tone,
  eyebrow,
  title,
  subtitle,
  packages,
  onCta,
}) {
  const dark = tone === "dark";
  return (
    <section
      id={id}
      data-testid={`section-${id}`}
      className={`scroll-mt-24 sm:scroll-mt-28 ${
        dark ? "bg-[#0F0F0E] text-[#F4F0E8]" : ""
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 py-20 sm:py-24 md:py-28 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-6 items-end mb-10 sm:mb-14 reveal">
          <div className="lg:col-span-7">
            <p className={`eyebrow ${dark ? "!text-clay" : ""}`}>{eyebrow}</p>
            <h2
              className={`font-display mt-5 text-3xl sm:text-4xl md:text-5xl lg:text-[3rem] leading-[1.05] tracking-[-0.02em] font-semibold ${
                dark ? "text-[#F4F0E8]" : "text-ink"
              }`}
            >
              {title}
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p
              className={`text-base md:text-lg leading-relaxed ${
                dark ? "text-[#BEB8AF]" : "text-ink-muted"
              }`}
            >
              {subtitle}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {packages.map((p) => (
            <PackageCard key={p.id} pkg={p} onCta={onCta} tone={tone} />
          ))}
        </div>
      </div>
    </section>
  );
}

export function PackageCard({ pkg, onCta, tone }) {
  const dark = tone === "dark";
  const emp = pkg.emphasized;
  return (
    <article
      id={pkg.id}
      data-testid={`package-${pkg.id}`}
      className={`reveal relative h-full scroll-mt-24 sm:scroll-mt-28 rounded-[32px] border p-7 sm:p-8 flex flex-col transition-all ${
        dark
          ? emp
            ? "bg-[#1A1A18] border-clay/60 shadow-[0_40px_90px_-40px_rgba(165,106,67,0.40)]"
            : "bg-[#151514] border-[#3C3935] hover:-translate-y-0.5 hover:border-[#A56A43]"
          : emp
          ? "bg-sand-50 border-clay/60 shadow-[0_30px_70px_-30px_rgba(165,106,67,0.35)]"
          : "bg-sand-50 border-sand-300 hover:-translate-y-0.5"
      }`}
    >
      {pkg.badge && (
        <span
          className={`absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] whitespace-nowrap ${
            dark
              ? "bg-[#A56A43] text-[#F4F0E8]"
              : "bg-clay text-sand-100"
          }`}
          data-testid={`package-badge-${pkg.id}`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${dark ? "bg-[#F4F0E8]" : "bg-sand-100"}`} />
          {pkg.badge}
        </span>
      )}

      <h3
        className={`font-display text-lg sm:text-xl md:text-[1.4rem] font-semibold tracking-tight ${
          dark ? "text-[#F4F0E8]" : "text-ink"
        }`}
      >
        {pkg.title}
      </h3>
      {pkg.price && (
        <div className="mt-3 flex items-baseline gap-2 flex-wrap">
          <span
            className={`font-display text-[1.6rem] sm:text-[1.8rem] md:text-[2rem] font-semibold tracking-[-0.02em] leading-none ${
              dark ? "text-[#F4F0E8]" : "text-ink"
            }`}
          >
            {pkg.price}
          </span>
          {pkg.priceLabel && (
            <span
              className={`font-mono text-[10px] uppercase tracking-[0.25em] ${
                dark ? "text-[#8E887F]" : "text-ink-muted"
              }`}
            >
              {pkg.priceLabel}
            </span>
          )}
        </div>
      )}
      <p
        className={`mt-3 text-[14px] sm:text-[14.5px] leading-relaxed ${
          dark ? "text-[#BEB8AF]" : "text-ink-muted"
        }`}
      >
        {pkg.description}
      </p>

      <p
        className={`mt-6 font-mono text-[10px] uppercase tracking-[0.28em] ${
          dark ? "text-[#A56A43]" : "text-clay"
        }`}
      >
        Built for
      </p>
      <ul
        className={`mt-3 space-y-1.5 text-[13.5px] leading-relaxed ${
          dark ? "text-[#BEB8AF]" : "text-ink"
        }`}
      >
        {pkg.builtFor.map((b) => (
          <li key={b} className="flex items-start gap-2">
            <span className={dark ? "text-[#8E887F]" : "text-ink-muted"}>·</span>
            <span>{b}</span>
          </li>
        ))}
      </ul>

      <p
        className={`mt-6 font-mono text-[10px] uppercase tracking-[0.28em] ${
          dark ? "text-[#8E887F]" : "text-ink-muted"
        }`}
      >
        Includes
      </p>
      <ul
        className={`mt-3 space-y-2 border-t pt-4 flex-1 ${
          dark ? "border-[#33302D]" : "border-sand-300"
        }`}
      >
        {pkg.includes.map((f) => (
          <li
            key={f}
            className={`flex items-start gap-3 text-[13.5px] leading-relaxed ${
              dark ? "text-[#F4F0E8]" : "text-ink"
            }`}
          >
            <span
              className={`mt-0.5 h-4 w-4 grid place-items-center rounded-full shrink-0 ${
                dark
                  ? "bg-[#B1744B]/15 text-[#B1744B]"
                  : emp
                  ? "bg-clay/15 text-clay"
                  : "bg-sand-200 text-olive"
              }`}
            >
              <Check size={10} strokeWidth={2.4} />
            </span>
            <span>{f}</span>
          </li>
        ))}
      </ul>

      {pkg.seo && (
        <div
          data-testid={`${pkg.id || "pkg"}-seo`}
          className={`mt-5 rounded-2xl border px-4 py-4 ${
            dark
              ? "border-[#33302D] bg-[#1A1815]"
              : "border-sand-300 bg-sand-100/70"
          }`}
        >
          <p
            className={`font-mono text-[9.5px] uppercase tracking-[0.28em] ${
              dark ? "text-[#A56A43]" : "text-clay"
            }`}
          >
            {pkg.seo.title}
          </p>
          <p
            className={`mt-1.5 text-[12.5px] leading-relaxed ${
              dark ? "text-[#BEB8AF]" : "text-ink-muted"
            }`}
          >
            {pkg.seo.description}
          </p>
          <ul
            className={`mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1.5 text-[12.5px] leading-snug ${
              dark ? "text-[#E2DCD1]" : "text-ink"
            }`}
          >
            {pkg.seo.bullets.map((b) => (
              <li key={b} className="flex items-start gap-2">
                <span
                  className={`mt-[5px] h-1 w-1 rounded-full shrink-0 ${
                    dark ? "bg-[#B1744B]" : emp ? "bg-clay" : "bg-olive"
                  }`}
                />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <button
        type="button"
        onClick={() => onCta && onCta(pkg)}
        data-testid={`package-cta-${pkg.id}`}
        className={`mt-7 group inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-3.5 text-[14px] font-medium transition-colors min-h-[48px] ${
          dark
            ? "bg-transparent text-[#F4F0E8] border-[1.5px] border-[#3C3935] hover:bg-[#1A1A18] hover:border-[#A56A43]"
            : emp
            ? "bg-ink-deep text-sand-100 hover:bg-ink"
            : "border border-ink-deep text-ink hover:bg-sand-200"
        }`}
      >
        {pkg.cta}
        <ArrowRight
          size={14}
          className="transition-transform group-hover:translate-x-0.5"
        />
      </button>
    </article>
  );
}

/* ---------- ComparisonSection ---------- */

const COMPARE_WEBSITE = [
  "You need a better online presence",
  "You do not have much lead flow yet",
  "You need a clean site before running campaigns",
  "Your main issue is presentation and credibility",
  "You need prospects to understand your services faster",
];

const COMPARE_REVENUE = [
  "You already get leads but lose them",
  "Your follow-up is inconsistent",
  "Your booking process is messy",
  "Your CRM is unorganized",
  "You need automation tied directly to revenue",
  "You want to reduce missed opportunities",
];

export function ComparisonSection({ ctaLabel = "Start Revenue Audit", onCta }) {
  const handle = onCta || openAudit;
  return (
    <section
      id="comparison"
      data-testid="comparison-section"
      className="scroll-mt-24 sm:scroll-mt-28 py-20 sm:py-24 md:py-28 px-5 sm:px-6 md:px-10 border-t border-sand-300"
    >
      <div className="max-w-7xl mx-auto">
        <div className="reveal text-center max-w-3xl mx-auto mb-10 sm:mb-14">
          <p className="eyebrow">Side by Side</p>
          <h2 className="font-display mt-5 text-3xl sm:text-4xl md:text-5xl leading-tight tracking-[-0.02em] font-semibold text-ink">
            Website Only{" "}
            <span className="italic text-ink-muted font-normal">vs</span>{" "}
            Revenue System
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
          <ComparisonColumn
            testId="comparison-website"
            title="Website Only"
            eyebrow="Best when"
            items={COMPARE_WEBSITE}
            tone="light"
          />
          <ComparisonColumn
            testId="comparison-revenue"
            title="Revenue System"
            eyebrow="Best when"
            items={COMPARE_REVENUE}
            tone="dark"
          />
        </div>

        <div className="reveal mt-10 sm:mt-12 text-center">
          <p className="text-[15px] sm:text-base text-ink-muted leading-relaxed">
            Not sure which one you need? Start with a Revenue Audit.
          </p>
          <button
            type="button"
            onClick={handle}
            data-testid="comparison-cta"
            className="mt-5 group inline-flex items-center justify-center gap-2 rounded-full bg-ink-deep text-sand-100 px-7 py-4 text-[15px] font-medium hover:bg-rust transition-colors min-h-[52px]"
          >
            {ctaLabel}
            <ArrowRight
              size={16}
              className="transition-transform group-hover:translate-x-0.5"
            />
          </button>
        </div>
      </div>
    </section>
  );
}

function ComparisonColumn({ testId, title, eyebrow, items, tone }) {
  const dark = tone === "dark";
  return (
    <div
      data-testid={testId}
      className={`reveal rounded-3xl border p-7 sm:p-9 md:p-10 ${
        dark
          ? "bg-ink-deep border-ink-deep text-sand-100"
          : "bg-sand-50 border-sand-300 text-ink"
      }`}
    >
      <h3
        className={`font-display text-2xl sm:text-[1.7rem] font-semibold tracking-tight ${
          dark ? "text-sand-100" : "text-ink"
        }`}
      >
        {title}
      </h3>
      <p
        className={`mt-5 font-mono text-[10px] uppercase tracking-[0.18em] text-clay`}
      >
        {eyebrow}
      </p>
      <ul className="mt-4 space-y-3">
        {items.map((it) => (
          <li
            key={it}
            className={`flex items-start gap-3 text-[14.5px] sm:text-[15px] leading-relaxed ${
              dark ? "text-sand-100" : "text-ink"
            }`}
          >
            <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-clay shrink-0" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---------- ProcessSection ---------- */

const PROCESS_STEPS = [
  {
    n: "01",
    title: "Diagnose",
    body:
      "We identify whether your main issue is your website, your follow-up, your booking flow, or the system behind it.",
  },
  {
    n: "02",
    title: "Map",
    body:
      "We map the buyer journey from first click to booked call, form submission, payment, or reactivation.",
  },
  {
    n: "03",
    title: "Build",
    body:
      "We build the website, landing page, CRM flow, automation, or revenue system required to fix the leak.",
  },
  {
    n: "04",
    title: "Launch",
    body:
      "We test the flow, clean up the mobile experience, connect tracking where needed, and prepare the system for real prospects.",
  },
];

export function ProcessSection({
  eyebrow = "The Build",
  title = "How the build works.",
  tone = "light",
}) {
  const dark = tone === "dark";
  return (
    <section
      id="process"
      data-testid="process-section"
      className={`scroll-mt-24 sm:scroll-mt-28 py-20 sm:py-24 md:py-28 px-5 sm:px-6 md:px-10 ${
        dark
          ? "bg-[#0F0F0E] text-[#F4F0E8]"
          : "border-t border-sand-300"
      }`}
    >
      <div className="max-w-7xl mx-auto">
        <div className="reveal text-center max-w-3xl mx-auto mb-10 sm:mb-14">
          <p className={`eyebrow ${dark ? "!text-[#A56A43]" : ""}`}>{eyebrow}</p>
          <h2
            className={`font-display mt-5 text-3xl sm:text-4xl md:text-5xl leading-tight tracking-[-0.02em] font-semibold ${
              dark ? "text-[#F4F0E8]" : "text-ink"
            }`}
          >
            {title}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {PROCESS_STEPS.map((s) => (
            <article
              key={s.n}
              data-testid={`process-step-${s.n}`}
              className={`reveal rounded-3xl border p-6 sm:p-7 flex flex-col h-full ${
                dark
                  ? "bg-[#151514] border-[#3C3935]"
                  : "bg-sand-50 border-sand-300"
              }`}
            >
              <span
                className={`font-mono text-[11px] uppercase tracking-[0.28em] ${
                  dark ? "text-[#A56A43]" : "text-clay"
                }`}
              >
                {s.n}
              </span>
              <h3
                className={`font-display mt-4 text-xl sm:text-[1.4rem] font-semibold tracking-tight ${
                  dark ? "text-[#F4F0E8]" : "text-ink"
                }`}
              >
                {s.title}
              </h3>
              <p
                className={`mt-3 text-[14px] leading-relaxed ${
                  dark ? "text-[#BEB8AF]" : "text-ink-muted"
                }`}
              >
                {s.body}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- FAQSection ---------- */

export function FAQSection({
  faqs,
  eyebrow = "FAQ",
  title = "Common questions.",
  tone = "light",
}) {
  const dark = tone === "dark";
  return (
    <section
      id="faq"
      data-testid="faq-section"
      className={`scroll-mt-24 sm:scroll-mt-28 py-20 sm:py-24 md:py-28 px-5 sm:px-6 md:px-10 ${
        dark ? "bg-[#0F0F0E] text-[#F4F0E8]" : "border-t border-sand-300"
      }`}
    >
      <div className="max-w-3xl mx-auto">
        <div className="reveal text-center mb-10 sm:mb-14">
          <p className={`eyebrow ${dark ? "!text-[#A56A43]" : ""}`}>{eyebrow}</p>
          <h2
            className={`font-display mt-5 text-3xl sm:text-4xl md:text-5xl leading-tight tracking-[-0.02em] font-semibold ${
              dark ? "text-[#F4F0E8]" : "text-ink"
            }`}
          >
            {title}
          </h2>
        </div>

        <div
          className={`reveal divide-y border-y ${
            dark ? "divide-[#33302D] border-[#33302D]" : "divide-sand-300 border-sand-300"
          }`}
        >
          {faqs.map((f, i) => (
            <FAQItem key={f.q} faq={f} index={i} dark={dark} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQItem({ faq, index, dark }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`faq-item-${index}`}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        aria-expanded={open}
        data-testid={`faq-toggle-${index}`}
        className="w-full flex items-center justify-between gap-6 py-6 sm:py-7 text-left group"
      >
        <span
          className={`font-display text-lg sm:text-xl leading-snug pr-4 ${
            dark ? "text-[#F4F0E8]" : "text-ink"
          }`}
        >
          {faq.q}
        </span>
        <span
          className={`shrink-0 h-9 w-9 grid place-items-center rounded-full border transition-colors ${
            open
              ? dark
                ? "bg-[#A56A43] text-[#F4F0E8] border-[#A56A43]"
                : "bg-ink-deep text-sand-100 border-ink-deep"
              : dark
              ? "bg-[#151514] text-[#F4F0E8] border-[#3C3935] group-hover:border-[#A56A43]"
              : "bg-sand-50 text-ink border-sand-300 group-hover:border-clay/60"
          }`}
        >
          {open ? <Minus size={15} /> : <Plus size={15} />}
        </span>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ease-out ${
          open ? "max-h-96 pb-6 sm:pb-7" : "max-h-0"
        }`}
      >
        <p
          className={`text-[14.5px] sm:text-[15px] leading-relaxed ${
            dark ? "text-[#BEB8AF]" : "text-ink-muted"
          }`}
        >
          {faq.a}
        </p>
      </div>
    </div>
  );
}
